import { useEffect, useRef, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { DeleteIcon, DownloadIcon, FoldDown, FoldRight } from "../../Static/icons/iconshere";
import { FETCH_AR_MODULE_DETAIL, UPDATE_AR_MODULE_DETAIL } from "../../apiClient/apiEndpoints";
import { useSelector } from "react-redux";
import Select from "react-select";
import apiClient from "../../apiClient";
import axios from "axios";
import { convertFileSize } from "../../utils/constants";
import { getAllowedMaxFileSize, getFileCategoryType, getFileSize, isFileSizeOverMaxSize } from "../../utils/common";
import { debounce } from "@mui/material";
import { BtnLoading } from "../../animations/BtnLoading";
import { AR_CONTENT_MEDIA } from "../../constant/limitSize";

const toTitleCase = (value) =>
  value
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

function getFileType(fileName) {
  const extension = fileName.split(".").pop().toLowerCase();

  const fileTypes = {
    image: ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"],
    video: ["mp4", "avi", "mov", "mkv", "flv", "wmv"],
    pdf: ["pdf"],
    document: ["doc", "docx", "txt", "odt", "rtf"],
    spreadsheet: ["xls", "xlsx", "csv", "ods"],
    presentation: ["ppt", "pptx", "odp"],
  };

  for (const [type, extensions] of Object.entries(fileTypes)) {
    if (extensions.includes(extension)) {
      return type;
    }
  }

  return "unknown";
}

let defaultFile = {
  fileName: null,
  size: null,
  type: null,
  file: null,
  url: null,
  uploading: false,
  progress: null,
  error: false,
};

const fileTypes = ["JPG", "JPEG", "PNG", "MP4", "PDF"];

const ARContentUpload = ({ uuid }) => {
  const configData = useSelector((state) => state.configSlice);
  const userData = useSelector((state) => state.userSlice);
  const [progress, setProgress] = useState(0);
  const [key, setKey] = useState(0);
  const [fileError, setFileError] = useState(null);
  const [fileToUpload, setFileToUpload] = useState({ ...defaultFile });
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [close, setClose] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const controllerRef = useRef(null);
  const UNO_URL = configData?.config?.UNO_URL;

  const [moduleDetails, setModuleDetails] = useState({
    media: [],
  });

  const fetchBasicModuleDetails = async () => {
    try {
      const detail = await apiClient.get(`${UNO_URL}${FETCH_AR_MODULE_DETAIL}/${uuid}`);
      setModuleDetails({
        media: detail?.versions?.[0]?.data?.media || [],
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchBasicModuleDetails();
  }, [uuid]);

  const handleSaveBasicDetails = async (mediaPayload) => {
    try {
      let updatedMedia = [];
      if (!mediaPayload) {
        updatedMedia = [
          ...(moduleDetails?.media || []),
          {
            fileName: fileToUpload.fileName,
            type: getFileType(fileToUpload.fileName),
            url: fileToUpload.url,
          },
        ];
      } else {
        updatedMedia = [...mediaPayload];
      }

      const payload = {
        version: {
          media: updatedMedia,
        },
      };
      await apiClient.post(`${UNO_URL}${UPDATE_AR_MODULE_DETAIL}/${uuid}`, payload);
      setFileToUpload({ ...defaultFile });
      setUploadedUrl("");
    } catch (error) {
      console.log(error);
    } finally {
      fetchBasicModuleDetails();
    }
  };


  const handleFileUpload = async (uploadedFile) => {
    if (!uploadedFile) return;
    setFileError(null);
    if (isFileSizeOverMaxSize(uploadedFile.size, AR_CONTENT_MEDIA)) {
      setKey((pre) => pre + 1);
      setFileError(`File size is more then ${getAllowedMaxFileSize(AR_CONTENT_MEDIA)}`);
      return
    }

    try {
      let fileType = getFileCategoryType(uploadedFile);

      const formData = new FormData();
      formData.append("file", uploadedFile);

      setFileToUpload((pre) => ({
        ...pre,
        uploading: true,
        fileName: uploadedFile.name,
        size: getFileSize(uploadedFile.size),
        file: uploadedFile,
        type: getFileCategoryType(uploadedFile),
      }));

      if (controllerRef.current) {
        controllerRef.current.abort(); // Cancel any previous request
      }
      controllerRef.current = new AbortController();

      const { data } = await axios.post(`${UNO_URL}/api/resource/ar/upload/${fileType}/${uuid}`, formData, {
        headers: {
          "Content-Type ": "multipart/form-data",
          Authorization: `Bearer ${userData?.token}`,
        },
        signal: controllerRef.current.signal,
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setFileToUpload((pre) => ({
            ...pre,
            progress: percent === 100 ? 99 : percent,
          }));
        },
      });

      setFileToUpload((pre) => ({
        ...pre,
        progress: 100,
        url: data.url,
        uploading: false,
      }));
    } catch (error) {
      setKey((pre) => pre + 1);
      setFileToUpload({
        ...defaultFile,
        error: true,
      });
      console.log(error);
    }
  };

  const handleDelete = (url) => {
    try {
      const mediaPayload = moduleDetails.media.filter((item) => item.url !== url);
      handleSaveBasicDetails(mediaPayload);
    } catch (error) {
      console.log(error);
    }
  };

  function downloadFile(url, fileName) {
    axios
      .get(url, {
        responseType: "blob",
      })
      .then((response) => {
        const blob = new Blob([response.data]);
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      })
      .catch((error) => {
        console.error("Download failed:", error);
      });
  }

  const handleFileCancel = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      console.log("Upload aborted by user");
      setFileToUpload({ ...defaultFile });
    }
  };

  const handleDebounceSave = debounce(() => handleSaveBasicDetails(null), 500, setSaveLoading);

  return (
    <div className={`detail-component  mt-3 ${close ? "closed" : ""} `}>
      <h6>
        Upload Media Content
        <span className="OpenCloseBtn" onClick={() => setClose((prev) => !prev)}>
          {!close ? <FoldDown /> : <FoldRight />}
        </span>{" "}
      </h6>

      <div className="uploadBoxParent">
        <div className="uploadBoxItem">
          <div className="uploadBoxHead">
            <p>Upload Media File</p>
          </div>
          <div className="fileuploader">
            {!fileToUpload.file ? (
              <FileUploader
                key={key}
                name="thumbnailInput"
                types={fileTypes}
                label="drag & drop or choose file to upload"
                handleChange={handleFileUpload}
              />
            ) : (
              <div className="file-uploadingView">
                <div className="uploadItem">
                  <p> {fileToUpload?.fileName} </p>
                  <div className="size-data">
                    <span className="fileSize"> {fileToUpload.size} </span>
                    <span className="percentage">{fileToUpload.progress}%</span>
                  </div>
                  <div className="uploadStatus">
                    <span className="complete" style={{ width: `${fileToUpload.progress}%` }}></span>
                  </div>
                  <div className="action-btns">
                    <button onClick={handleFileCancel}>Cancel</button>
                    <button disabled={fileToUpload.url ? false : true} onClick={handleDebounceSave}>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <p class="error">{fileError}</p>
          <div class="uploadpromt">File format accepted: {fileTypes.join(", ")} and Max file size: {getAllowedMaxFileSize(AR_CONTENT_MEDIA)}</div> 
        </div>

        <div className="uploadBoxItem">
          <div className="uploadBoxHead">
            <p>Media List</p>
          </div>
          <div className="mediaList">
            <div className="table-responsive workflowtb mt-2 stickyhead ">
              <table className="table align-middle table-nowrap mb-0">
                <thead>
                  <tr>
                    <th className="text-center">Thumbail</th>
                    <th className="w-50">File Name</th>
                    <th className="text-center">File Type</th>
                    <th className="text-center"> Actions </th>
                  </tr>
                </thead>
                <tbody className="versiontable">
                  {moduleDetails.media?.map((mediaFile) => (
                    <tr key={mediaFile.url}>
                      <td>
                        <div className="thumbnail-here">
                          {mediaFile?.type === "image" && <img src={mediaFile?.url} referrerPolicy="no-referrer" />}
                          {mediaFile?.type === "video" && (
                            <video src={mediaFile?.url} controls autoPlay={false} referrerPolicy="no-referrer" />
                          )}
                        </div>
                      </td>
                      <td>{mediaFile?.fileName}</td>
                      <td className="text-center">{toTitleCase(mediaFile?.type || "")}</td>
                      <td className="text-center">
                        <div className="table-action-btns">
                          <span onClick={() => handleDelete(mediaFile.url)}>
                            <DeleteIcon />
                          </span>
                          <span onClick={() => downloadFile(mediaFile.url, mediaFile?.fileName)}>
                            <DownloadIcon />
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ARContentUpload;
